'use client'

import { HomepageQueryResult, PageQueryResult } from "@/sanity.types";
import ColorScheme from "./colorScheme";
import { useEffect, useRef } from "react";
import { animate, useScroll, useMotionValueEvent } from "motion/react";
import CustomPortableText from "@/components/portable-text";
import { PortableTextBlock } from "next-sanity";
import Image from "next/image";

type SplitTextStickyImagesProps = Extract<NonNullable<NonNullable<HomepageQueryResult>["content"]>[number], { _type: "splitTextStickyImages" }> | Extract<NonNullable<NonNullable<PageQueryResult>["content"]>[number], { _type: "splitTextStickyImages" }>;
type BlockProps = NonNullable<SplitTextStickyImagesProps["textStickyImageBlocks"]>[0]

const Slide = ({index, totalNum, block} : {index: number, totalNum: number, block : BlockProps}) => {
  const {heading, content, firstImage, secondImage} = block;
  const slideRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({ target: slideRef, offset: ["start center", "start end"] });
  const updateSlide = (current: number) => {
    if (slideRef.current) {
      if (current < (3.375 * parseFloat(getComputedStyle(document.documentElement).fontSize) / window.innerHeight + 0.01)) {
        if (slideRef.current.classList.contains('z-10')) {
          slideRef.current.classList.remove('z-10');
        }

        if (slideRef.current.parentElement?.previousElementSibling) {
          animate(slideRef.current.parentElement.previousElementSibling, { opacity: 0 })
        }
      } else {
        if (!slideRef.current.classList.contains('z-10')) {
          slideRef.current.classList.add('z-10');
        }

        if (slideRef.current.parentElement?.previousElementSibling) {
          animate(slideRef.current.parentElement.previousElementSibling, { opacity: current })
        }
      }
    }
  }

  // to prevent fouc
  useEffect(() => {
    updateSlide(scrollYProgress.get());
    if (slideRef.current?.parentElement) {
      slideRef.current.parentElement.classList.remove('opacity-0');
    }
  }, [])

  useMotionValueEvent(scrollYProgress, "change", (current) => {
    updateSlide(current);
  })

  return (
    <div
      className="absolute bottom-0 w-full opacity-0"
      style={{ height: `${(totalNum - index) * 200}dvh`}}
    >
      <div ref={slideRef} className="z-10 sticky top-13.5 w-full h-[calc(100dvh-3.375rem)] p-5 md:p-7.5 marketing-container max-md:flex max-md:flex-col max-md:gap-7.5 md:grid md:grid-cols-12 max-md:bg-(--color-background)">
        <div className="md:col-span-4 self-center">
          {heading && <h3 className="whitespace-pre-wrap mb-7.5 text-box-trim-both">{heading}</h3>}
          {content && <CustomPortableText value={content as PortableTextBlock[]} />}
        </div>
        <div className="max-md:grow md:row-start-1 md:col-start-8 md:col-span-5 relative">
          {firstImage && firstImage.assetPath &&
            <Image
              src={firstImage.assetPath}
              alt={firstImage.caption || ""}
              fill={true}
              loading="lazy"
              sizes="(min-width: 1024px) 80vw, 200vw"
              className="object-cover"
            />
          }
        </div>
      </div>
      <div className="pointer-events-none sticky top-13.5 w-full h-[calc(100dvh-3.375rem)] p-5 md:p-7.5 marketing-container max-md:flex max-md:flex-col max-md:gap-7.5 md:grid md:grid-cols-12">
        <div className="invisible md:hidden">
          {heading && <h3 className="whitespace-pre-wrap mb-7.5 text-box-trim-both">{heading}</h3>}
          {content && <CustomPortableText value={content as PortableTextBlock[]} />}
        </div>
        <div
          className="max-md:grow md:row-start-1 md:col-start-8 md:col-span-5 relative"
        >
          {secondImage && secondImage.assetPath &&
            <Image
              src={secondImage.assetPath}
              alt={secondImage.caption || ""}
              fill={true}
              loading="lazy"
              sizes="(min-width: 1024px) 80vw, 200vw"
              className="pointer-events-auto object-cover"
            />
          }
        </div>
      </div>
    </div>
  )
}

export default function SplitTextStickyImages(props: SplitTextStickyImagesProps) {
  const {textStickyImageBlocks, colorScheme} = props;
  return (
    <ColorScheme colorScheme={colorScheme && colorScheme != 'default'? colorScheme : 'pea'}>
      <div
        className="bg-(--color-background) text-(--color-foreground) md:pb-7.5"
      >
        {textStickyImageBlocks &&
          <div
            className="relative after:content-[''] after:pointer-events-none after:block after:h-[calc(100dvh-3.375rem)] after:w-full after:sticky after:top-13.5 after:border-(--color-background) after:border-[1.25rem] after:md:border-[1.875rem]"
            style={{ height: `${textStickyImageBlocks.length * 200}dvh`}}
          >
            {textStickyImageBlocks.map((block, i) =>
              <Slide key={i} index={i} totalNum={textStickyImageBlocks.length} block={block}/>
            )}
          </div>
        }
      </div>
    </ColorScheme>
  );
}